import React, { useRef, useState, useEffect } from 'react';
import {
  View,
  Heading,
  Text,
  Flex,
  IconFlagLine,
  IconNoteLine,
  IconAdminToolsLine,
  IconChartLineLine,
  IconLineReaderLine,
  Metric,
  MetricGroup,
  Spinner,
} from '@instructure/ui';
import { usePlayerStore, getStore } from '../../../../../stores/StoreContext';
import HlsFragment from '../../../VideoRenderer/HlsFragment';
import { useHotkeys } from 'react-hotkeys-hook';

// Import our modular components
import VideoControls from './VideoControls';
import ReviewerTools from './ReviewerTools';
import InPlaceEdit from '../../../../Common/InPlaceEdit';
import ReviewerComments from './ReviewerComments';

const AnnotationViewer = ({
  positionTarget,
  top,
  left,
  setShowContextMenu,
  annotation,
  onStartAdjustment,
  onClose = () => {},
  ...props
}) => {
  // State management
  const [localAnnotation, setLocalAnnotation] = useState({ ...annotation });
  const [leftPosition, setLeftPosition] = useState(left);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fragmentId, setFragmentId] = useState(null);

  // References
  const hlsFragmentRef = useRef(null);
  const leadInTime = 10;
  const leadOutTime = 5;

  const targetRef = useRef(null);

  // Initialize start and end times based on annotation
  useEffect(() => {
    setStartTime(annotation.start - leadInTime);
    setEndTime(annotation.start + annotation.duration + leadOutTime);
  }, [annotation]);

  // Get fragmentId from the HlsFragment ref once it's initialized
  useEffect(() => {
    // Small delay to ensure the HlsFragment is fully mounted and initialized
    const timer = setTimeout(() => {
      if (hlsFragmentRef.current && hlsFragmentRef.current.fragmentId) {
        // Set the fragmentId from the ref
        setFragmentId(hlsFragmentRef.current.fragmentId);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [hlsFragmentRef.current]);

  // Helper function to remove pluralization and lowercase the word - memoized to prevent recreation
  const nonPluralize = React.useCallback(
    (str) => str.replace('s', '').toLowerCase(),
    []
  );
  // Get session tracks from store
  const SessionTracks = usePlayerStore((state) => state.tracks);
  const ExamSessionStore = getStore('examSessionStore');

  // Keyboard shortcuts
  useHotkeys('arrowleft', () => {
    hlsFragmentRef.current.stepBackward();
  });
  useHotkeys('arrowright', () => {
    hlsFragmentRef.current.stepForward();
  });
  useHotkeys('space', () =>
    isPlaying ? hlsFragmentRef.current.pause() : hlsFragmentRef.current.play()
  );

  useHotkeys('esc', () => {
    setShowContextMenu(false);
  });

  // Calculate progress percentage - memoized to prevent recalculation on every render
  const { fragmentDuration, elapsedTime, progressPercentage } =
    React.useMemo(() => {
      const fragmentDuration = endTime - startTime;
      const elapsedTime = Math.max(
        0,
        Math.min(currentTime - startTime, fragmentDuration)
      );
      const progressPercentage =
        fragmentDuration > 0 ? (elapsedTime / fragmentDuration) * 100 : 0;
      return { fragmentDuration, elapsedTime, progressPercentage };
    }, [currentTime, startTime, endTime]);

  // calls to confirm this is a valid annotation...
  const handleConfirm = React.useCallback(async () => {
    // Implement delete functionality
    const result = await ExamSessionStore.updateAnnotation(annotation.sid, {
      //TODO: Update the users name...
      reviewedBy: 'admin',
      reviewedDate: new Date().toISOString(),
      updatedBy: 'admin',
      updatedDate: new Date().toISOString(),
    });
  }, [ExamSessionStore, annotation.sid]);

  const handleUnreview = React.useCallback(async () => {
    // Implement delete functionality
    const result = await ExamSessionStore.updateAnnotation(annotation.sid, {
      //TODO: Update the users name...
      reviewedBy: null,
      reviewedDate: null,
      updatedBy: 'admin',
      updatedDate: new Date().toISOString(),
    });
  }, [ExamSessionStore, annotation.sid]);

  const handleDelete = React.useCallback(async () => {
    // Implement delete functionality
    const result = await ExamSessionStore.deleteAnnotation(annotation.sid);
    setShowContextMenu(false);
  }, [ExamSessionStore, annotation.sid, setShowContextMenu]);

  // Handle details change
  const handleDetailsChange = async ({ oldText, newText }) => {
    try {
      const updateResult = await ExamSessionStore.updateAnnotation(
        annotation.sid,
        {
          details: newText,
          //TODO: Update the users name...
          updatedBy: 'admin',
          updatedDate: new Date().toISOString(),
        }
      );
      if (!updateResult.successful) {
        return { success: false, message: 'Failed to update details' };
      }

      // For now, we'll simulate a successful update
      return { success: true };
    } catch (error) {
      // If there's an error, return it to display in the UI
      console.log(error);
      return {
        success: false,
        message: error.message || 'Failed to update details',
      };
    }
  };

  // Memoize the icon rendering to prevent unnecessary re-renders
  const renderIcon = React.useCallback(() => {
    if (annotation.type.categoryId === 'flags') {
      return <IconFlagLine />;
    } else if (annotation.type.categoryId === 'notes') {
      return <IconNoteLine />;
    } else if (annotation.type.categoryId === 'events') {
      return <IconAdminToolsLine />;
    } else if (annotation.type.categoryId === 'network') {
      return <IconChartLineLine />;
    } else if (annotation.type.categoryId === 'phases') {
      return <IconLineReaderLine />;
    }
    return null;
  }, [annotation.type.categoryId]);
  return (
    <>
      <div
        ref={targetRef}
        style={{
          position: 'absolute',
          left: left,
          top: top,
          width: '1px',
          height: '1px',
          visibility: 'hidden',
        }}
      />

      {startTime && endTime && (
        <View
          padding="small"
          display="block"
          as="div"
          maxWidth="600px"
          width="600px"
        >
          {/* Header */}
          <Flex as="div" withVisualDebug={false} margin="none none small none">
            <Flex.Item shouldGrow>
              <Heading level="h3">
                {renderIcon()} {annotation.type.name}
              </Heading>
            </Flex.Item>
            <Flex.Item>
              <ReviewerTools
                annotation={annotation}
                onConfirm={handleConfirm}
                onUnreview={handleUnreview}
                onDelete={handleDelete}
                onClose={onClose}
                setShowContextMenu={setShowContextMenu}
              />
            </Flex.Item>
          </Flex>

          {/* Video and Controls */}
          <Flex as="div">
            <Flex.Item>
              <HlsFragment
                ref={hlsFragmentRef}
                width={400}
                height={300}
                startTime={startTime}
                leadInTime={leadInTime}
                leadOutTime={leadOutTime}
                endTime={endTime}
                onReady={(fragmentId) => {
                  setFragmentId(fragmentId);
                }}
              />
            </Flex.Item>

            <Flex.Item shouldGrow>
              <View as="div" width="100%" textAlign="center">
                <View as="div" margin="none none large none">
                  <MetricGroup>
                    <Metric
                      renderLabel="Score"
                      renderValue={annotation?.score}
                    />
                    <Metric
                      renderLabel="Duration"
                      renderValue={
                        annotation?.duration ? annotation.duration + 's' : '-'
                      }
                    />
                  </MetricGroup>
                </View>
                {/* Video Controls */}
                <VideoControls fragmentId={fragmentId} />
                <View as="div" margin="small 0">
                  <Text as="div" variant="contentSmall">
                    Created By:
                  </Text>
                  <Text as="div" variant="contentSmall">
                    {annotation?.createdBy}
                  </Text>
                </View>
              </View>
            </Flex.Item>
          </Flex>

          {/* Annotation Details */}
          <Text as="div" variant="contentSmall">
            {/* TODO: Wrap this in permissions... */}
            <InPlaceEdit
              text={annotation?.details}
              placeholder={`Provide additional details about this ${nonPluralize(annotation.type.categoryId)}...`}
              onChange={handleDetailsChange}
            />
          </Text>

          {/* Reviewer Tools */}
          {/* TODO: Wrap this in permissions... */}
          <ReviewerComments annotation={annotation} />
        </View>
      )}
    </>
  );
};

export default AnnotationViewer;
